/**
 * @file PatientHPOField.js
 * @description multiselection with api search.  no biocurated options.
 * 
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since May 27, 2022 18:45
 */
import axiosClient from 'src/services/axiosClient.js';
import debounce from 'lodash/debounce';

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Autocomplete } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';

import { arrayExists, stringExists } from 'src/@common/utilities';

const callApi = (searchTerm) => {

    return new Promise((resolve, reject) => {

        if (stringExists(searchTerm)) {

            const baseUrl = import.meta.env.VITE_APP_API_BASE_URL;

            const config = {
                method: 'post',
                baseURL: baseUrl,
                url: '/hpo/hpoSearch',
                data: {
                    searchTerm,
                },
            };

            axiosClient(config)
                .then((response) => {
                    resolve(response.data.payload.queryResults);
                })
                .catch((err) => {
                    reject(err);
                });

        } else {
            resolve();
        }

    });
}

function PatientHPOField(props) {
    const {
        fieldName = 'patientHPO',
        fieldLabel = 'Patient HPO',
        currentValues,  // object array
        fieldErrors = {},
        helpText = '',
        isFocus = false,
        isRequired = false,
        sxStyle = null,
        handleBlur = () => { },
        handleChange,
    } = props;

    const [helperText, setHelperText] = useState('');
    const [noOptionsText, setNoOptionsText] = useState('Enter a search term');
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [values, setValues] = useState([]);
    const [loading, setLoading] = useState(false);

    const debouncedSearch = useMemo(
        () =>
            debounce((text) => {

                setNoOptionsText('Loading...');
                setLoading(true)
                setOptions([]);

                callApi(text)
                    .then((results) => {

                        const newOptions = [];

                        if (arrayExists(results)) {
                            results.forEach((item) => {
                                newOptions.push(item);
                            });
                        } else {
                            setNoOptionsText(`No HPO Terms found for: ${text}`);
                        }

                        setOptions(newOptions);
                        setLoading(false);

                    }).catch((err) => {
                        console.error(err);
                        setLoading(false);
                    });
            }, 800),
        []
    )

    // const getOptionsDelayed = useCallback(
    //     debounce((text) => {

    //         if (stringExists(text)) {
    //             callApi(text)
    //                 .then((results) => {

    //                     let newOptions = [];

    //                     if (arrayExists(results)) {

    //                         results.forEach((item) => {
    //                             newOptions.push(item);
    //                         });

    //                         setNoOptionsText('Enter a search term');

    //                     } else {
    //                         setNoOptionsText(`No HPO Terms found for: ${text}`);
    //                     }

    //                     setOptions(newOptions);
    //                 });

    //         } else {
    //             setNoOptionsText('Enter a search term');
    //         }

    //     }, 800),
    //     [],
    // );

    useEffect(() => {
        // set the local values using the current values
        if (arrayExists(currentValues)) {

            const values = [];

            currentValues.forEach((currentValue) => {
                values.push({
                    name: currentValue.name,
                    id: currentValue.id,
                });
            });

            setValues(values);
        }

    }, [currentValues]);

    useEffect(() => {

        if (fieldErrors[fieldName]) {
            setHelperText(fieldErrors[fieldName])

        } else {

            if (stringExists(helpText)) {
                setHelperText(helpText);
            } else {
                setHelperText('')
            }

        }

    }, [fieldErrors, helpText, fieldName]);

    const onChange = (event, newValues) => {
        // this change event provides the newly selected object.
        // update both the local and parent state.

        // set the local state
        setValues(newValues);

        // wrap in a synthetic event
        const newEvent = {
            target: {
                type: 'autocomplete',
                value: newValues,
            }
        };

        // call the parent change handler
        handleChange(newEvent, 'patientHPO');
    };

    const onInputChange = (event, newValue, reason) => {
        // using this changeEvent to identify keystroke input,
        // indicating a needed call to the api to provide new options 

        switch (reason) {
            case 'clear':
                break;

            case 'reset':
                break;

            case 'input':

                // keystroke input.  hit the api
                setNoOptionsText('Loading...');

                debouncedSearch(newValue, (apiOptions) => {

                    if (!arrayExists(apiOptions)) {
                        setOptions([])
                        setNoOptionsText('No HPO Terms found');
                    } else {

                        const transformedOptions = [];

                        apiOptions.forEach((option) => {
                            transformedOptions.push(option);
                        });

                        setNoOptionsText('Enter a search term');
                        setOptions(transformedOptions);
                    }

                });

                break;

            default:
                break;
        }
    };

    return (
        <Autocomplete
            id={fieldName}
            name={fieldName}
            disablePortal={true}
            filterSelectedOptions
            multiple
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            getOptionLabel={(option) => {
                return option.name
            }}
            filterOptions={(x) => x}  // must disable filtering on client
            includeInputInList
            isOptionEqualToValue={(option, value) => {
                return option.id === value.id
            }}
            onBlur={() => {
                handleBlur(fieldName);
            }}
            onInputChange={onInputChange}
            onChange={onChange}
            options={options}
            loading={loading}
            noOptionsText={noOptionsText}
            value={values}
            sx={sxStyle}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={fieldLabel}
                    size="small"
                    margin="dense"
                    variant="filled"
                    error={Boolean(fieldErrors[fieldName])}
                    autoFocus={isFocus}
                    helperText={helperText}
                    required={isRequired}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}

PatientHPOField.propTypes = {
    fieldLabel: PropTypes.string,
    fieldName: PropTypes.string,
    currentValues: PropTypes.array,
    geneCurations: PropTypes.array,
    fieldErrors: PropTypes.object,
    helpText: PropTypes.string,
    isFocus: PropTypes.bool,
    isRequired: PropTypes.bool,
    sxStyle: PropTypes.object,
    handleBlur: PropTypes.func,
    handleChange: PropTypes.func.isRequired,
};

export default PatientHPOField;
